import React, { useEffect, useRef, useState } from "react";
import BackgroundLayout from "../Components/BackgroundLayout";
import Navbar from "../Components/Navbar";
import NavbarGenres from "../Components/NavbarGenres";
import {
  useGetDataByGenreIdQuery,
  useGetMovieGenreListQuery,
  useGetTrendngDataQuery,
} from "../Slices/httpSlice";
import SectionDragCards, {
  fetchedDataType,
} from "../Components/Drag-Cards/SectionDragCards";
import { useAppDispatch, useAppSelector } from "../Store/hooks";
import classes from "./Movies.module.css";
import DragCardsTitle from "../Components/Drag-Cards/DragCardsTitle";
import SkeletonCardsGrid from "../Components/SkeletonCardsGrid";
import MovieCard from "../Components/Drag-Cards/MovieCard";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { resetMovieGenre } from "../Slices/movieGenreSlice";
import NavbarMobile from "../Components/NavbarMobile";
import BottomNavbar from "../Components/BottomNavbar";
import AuthModal from "../Components/Modals/AuthModal";
import SearchModal from "../Components/SearchModal";
import WatchlistModal from "../Components/WatchlistModal";
import UserRatingsModal from "../Components/UserRatingsModal";
import ProfileModal from "../Components/ProfileModal";
import Drawer from "../Components/Drawer";

const Movies = () => {
  const [windowWidth, setWindowWidth] = useState<number>(0);
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const resetState: undefined | true = location.state?.reset;
    if (resetState) {
      dispatch(resetMovieGenre());
    }
  }, [location]);

  useEffect(() => {
    const updateConstraints = () => {
      setWindowWidth(window.innerWidth);
    };

    updateConstraints();
    window.addEventListener("resize", updateConstraints);

    return () => {
      window.removeEventListener("resize", updateConstraints);
    };
  }, []);

  const { movieGenre, movieGenreId } = useAppSelector(
    (state) => state.movieGenreSlice
  );

  const [pageNumber, setPageNumber] = useState<number>(1);

  const [genreDataArray, setGenreDataArray] = useState<[] | fetchedDataType[]>(
    []
  );

  const [fetchData, setFetchData] = useState<boolean>(false);

  const lastItemRef = useRef<HTMLDivElement | null>(null);

  const {
    data: movieGenresList,
    isFetching: genreLoading,
    error: genreError,
  } = useGetMovieGenreListQuery(null);

  const {
    data: trending,
    refetch: trendingRefetch,
    isFetching: trendingLoading,
    error: trendingError,
  } = useGetTrendngDataQuery("movie");

  const {
    data: adventure,
    refetch: adventureRefetch,
    isFetching: adventureLoading,
    error: adventureError,
  } = useGetDataByGenreIdQuery({
    media: "movie",
    genreId: 12,
    pageNumber: 1,
  });

  const {
    data: comedy,
    refetch: comedyRefetch,
    isFetching: comedyLoading,
    error: comedyError,
  } = useGetDataByGenreIdQuery({ media: "movie", genreId: 35, pageNumber: 1 });

  const {
    data: scific,
    refetch: scificRefetch,
    isFetching: scificLoading,
    error: scificError,
  } = useGetDataByGenreIdQuery({
    media: "movie",
    genreId: 878,
    pageNumber: 1,
  });

  const {
    data: thriller,
    refetch: thrillerRefetch,
    isFetching: thrillerLoading,
    error: thrillerError,
  } = useGetDataByGenreIdQuery({
    media: "movie",
    genreId: 53,
    pageNumber: 1,
  });

  const {
    data: animation,
    refetch: animationRefetch,
    isFetching: animationLoading,
    error: animationError,
  } = useGetDataByGenreIdQuery({
    media: "movie",
    genreId: 16,
    pageNumber: 1,
  });

  const {
    data: mystery,
    refetch: mysteryRefetch,
    isFetching: mysteryLoading,
    error: mysteryError,
  } = useGetDataByGenreIdQuery({
    media: "movie",
    genreId: 9648,
    pageNumber: 1,
  });

  const {
    data: documentary,
    refetch: documentaryRefetch,
    isFetching: documentaryLoading,
    error: documentaryError,
  } = useGetDataByGenreIdQuery({
    media: "movie",
    genreId: 99,
    pageNumber: 1,
  });

  const {
    data: western,
    refetch: westernRefetch,
    isFetching: westernLoading,
    error: westernError,
  } = useGetDataByGenreIdQuery({
    media: "movie",
    genreId: 37,
    pageNumber: 1,
  });

  const {
    data: genreData,
    refetch: genreDataRefetch,
    isFetching: genreDataLoading,
    error: genreDataError,
  } = useGetDataByGenreIdQuery(
    {
      media: "movie",
      genreId: movieGenreId,
      pageNumber: pageNumber,
    },
    {
      skip: movieGenreId === null,
    }
  );

  useEffect(() => {
    setPageNumber(1);
    setFetchData(false);
    setGenreDataArray([]);
  }, [movieGenreId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && movieGenreId !== null && !fetchData) {
          if (pageNumber <= 16) {
            genreDataRefetch();
            setFetchData(true);
          }
        }
      },
      { threshold: 1.0 }
    );

    if (lastItemRef.current) {
      observer.observe(lastItemRef.current);
    }

    return () => {
      if (lastItemRef.current) {
        observer.unobserve(lastItemRef.current);
      }
    };
  }, [movieGenreId, pageNumber, fetchData]);

  useEffect(() => {
    if (fetchData && !genreDataLoading) {
      if (movieGenreId !== null && genreData) {
        setPageNumber((prevValue) => prevValue + 1);
        setGenreDataArray((prevArray) => {
          return prevArray.length > 0 ? [...prevArray, genreData] : [genreData];
        });
        setFetchData(false);
      }
    }
  }, [fetchData, genreData, genreDataLoading]);

  return (
    <>
      <BackgroundLayout paddingBottom={true}>
        <AuthModal />
        <NavbarMobile />
        <Navbar activeLink={2} />
        <NavbarGenres
          genreType="movies"
          genres={movieGenresList}
          genreLoading={genreLoading}
        />

        {movieGenre === "all" ? (
          <SectionDragCards
            hideFilter={true}
            // style={{ paddingBottom: "20px" }}
            trending={trending}
            adventure={adventure}
            comedy={comedy}
            scific={scific}
            thriller={thriller}
            animation={animation}
            mystery={mystery}
            documentary={documentary}
            western={western}
            trendingLoading={trendingLoading}
            adventureLoading={adventureLoading}
            comedyLoading={comedyLoading}
            scificLoading={scificLoading}
            thrillerLoading={thrillerLoading}
            animationLoading={animationLoading}
            documentaryLoading={documentaryLoading}
            westernLoading={westernLoading}
            mysteryLoading={mysteryLoading}
            trendingError={trendingError}
            adventureError={adventureError}
            comedyError={comedyError}
            scificError={scificError}
            thrillerError={thrillerError}
            animationError={animationError}
            mysteryError={mysteryError}
            documentaryError={documentaryError}
            westernError={westernError}
            checkAndNavigate={false}
            navigateToMovies={true}
            navigateToSeries={false}
          />
        ) : (
          <div className={classes.section_drag_cards}>
            <div className={classes.section_cards}>
              <DragCardsTitle
                  // Popular Movies in 
                title={`
                  လူကြိုက်များသော ရုပ်ရှင်များ
                  ${movieGenre}`}
                hideFilter={true}
              />

              {genreDataArray?.map((data, idx) => {
                return (
                  <motion.div
                    key={idx}
                    className={classes.section_card}
                    style={{
                      gridTemplateColumns: `repeat(${genreDataArray[idx]?.result?.results?.length}, 150px)`,
                    }}
                    drag="x"
                    dragConstraints={{
                      top: 0,
                      left: -(windowWidth <= 800
                        ? 10 +
                          genreDataArray[idx]?.result?.results.length * 150 +
                          genreDataArray[idx]?.result?.results.length * 10 +
                          genreDataArray[idx]?.result?.results.length * 1 -
                          windowWidth
                        : window.innerWidth * 0.05 +
                          genreDataArray[idx]?.result?.results.length * 150 +
                          genreDataArray[idx]?.result?.results.length * 10 +
                          genreDataArray[idx]?.result?.results.length * 1 -
                          windowWidth),
                      right: 0,
                      bottom: 0,
                    }}
                    transition={{
                      type: "spring",
                      damping: 100,
                      stiffness: 100,
                    }}
                  >
                    {data?.result?.results?.map((dataItem, idx2) => (
                      <MovieCard
                        key={`${idx}-${idx2}`}
                        data={dataItem}
                        navigateTo="movies"
                      />
                    ))}
                  </motion.div>
                );
              })}

              {genreDataLoading && <SkeletonCardsGrid />}
              {pageNumber === 16 ? null : (
                <div ref={lastItemRef} className={classes.load_item}></div>
              )}
            </div>
          </div>
        )}
      </BackgroundLayout>
      <BottomNavbar activeLink={2} />
      <SearchModal />
      <WatchlistModal />
      <UserRatingsModal />
      <ProfileModal />
      <Drawer />


    </>
  );
};

export default Movies;

// const [data, setData] = useState<any[]>([
//   {
//     data: [],
//     pageNumber: 1,
//     error: null,
//     loading: true,
// leftConstraint:0,
//   },
// ]);
